import { CubeLogo } from '@/components/custom-ui/3d-cube-logo'
import { FullPageSpinner, SmallSpinner } from '@/components/loading_states/generic-spinner'
import { useAuth } from '@/components/providers/auth-provider'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { sleep } from '@/lib/utils/generic-utils'
import { useTRPC } from '@/trpc/react'
import { COLORS } from '@repo/shared_utils'
import { APP_NAME } from '@repo/shared_utils/constants'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertCircle } from 'lucide-react'
import { Suspense, useState } from 'react'
import { FaGoogle } from 'react-icons/fa'
import { toast } from 'sonner'

export const Route = createFileRoute('/login')({
  component: RouteComponent,
})

function RouteComponent() {
  const api = useTRPC()
  const login = useMutation({
    ...api.user.login.mutationOptions({
      onSuccess: (data) => {
        console.log('>>> inside onSuccess')
        console.log({ data })
        if (!window || !window.location) return
        window.location.href = data
      },
      onError: (error) => {
        console.log('>>> inside onError')
        console.log({ error })
        toast.error('Login failed', {
          description: error.message,
        })
      },
    }),
  })

  const { isAuthenticated, isUserOnboarded } = useAuth()
  const [loading, setLoading] = useState(false)
  const [devBypassSecret, setDevBypassSecret] = useState('')

  //   const search = useSearch()
  //   const {error} = search

  // if (isAuthenticated && isUserOnboarded) redirect('/dashboard')

  const handleGoogleLogin = async () => {
    // clear the user data and tokens first before logging in
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      })
      await sleep(300)
    } catch (error) {
      console.error('Error logging out', error)
    }

    setLoading(true)
    await sleep(300)
    login.mutate()
  }

  return (
    <div className="flex min-h-screen">
      {/* Left side: Login form - Light bg in light mode, dark bg in dark mode */}
      <div className="flex flex-1 items-center justify-center bg-gradient-to-br from-gray-50 via-white to-indigo-50/30 dark:bg-gradient-to-br dark:from-slate-900 dark:via-indigo-900/90 dark:to-purple-900/80 relative overflow-hidden">
        {/* Subtle background decorative elements */}
        <div className="absolute inset-0">
          <div
            className="absolute top-10 left-10 w-32 h-32 rounded-full opacity-5 blur-2xl"
            style={{ backgroundColor: COLORS[1] }}
          />
          <div
            className="absolute bottom-10 right-10 w-40 h-40 rounded-full opacity-5 blur-2xl"
            style={{ backgroundColor: COLORS[2] }}
          />
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-3 blur-3xl"
            style={{ backgroundColor: COLORS[0] }}
          />
        </div>

        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, ${COLORS[1]} 1px, transparent 0)`,
              backgroundSize: '50px 50px',
            }}
          />
        </div>

        <Card className="w-full border-0 max-w-md shadow-2xl bg-white/80 dark:bg-white/90 dark:backdrop-blur-md dark:shadow-2xl relative z-10">
          {/* Subtle border glow */}
          <div
            className="absolute inset-0 rounded-2xl border border-gray-200/50 dark:border-gray-300/40"
            style={{
              boxShadow: `0 0 0 1px ${COLORS[0]}10, 0 0 20px ${COLORS[0]}05`,
            }}
          />

          <CardHeader className="space-y-6 pb-8">
            <div className="flex flex-col items-center gap-4">
              <CardTitle className="text-3xl text-center bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 bg-clip-text text-transparent font-bold">
                {APP_NAME}
              </CardTitle>
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-xs">
                Sign in to access your payroll dashboard
              </p>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            <Suspense fallback={<FullPageSpinner />}>
              <LoginError error={null} />
            </Suspense>
            {isAuthenticated && isUserOnboarded ? (
              <Button
                asChild
                className="w-full text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 h-12 text-base z-1000"
                style={{
                  backgroundColor: COLORS[0],
                  boxShadow: `0 4px 15px ${COLORS[0]}30`,
                }}
              >
                <Link to="/dashboard">Proceed to Portal</Link>
              </Button>
            ) : (
              <Button
                className="w-full cursor-pointer font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 h-12 text-base z-1000"
                variant="outline"
                onClick={handleGoogleLogin}
                style={{
                  borderColor: COLORS[1],
                  color: COLORS[1],
                  backgroundColor: 'transparent',
                  boxShadow: `0 4px 15px ${COLORS[1]}20`,
                }}
              >
                {loading ? <SmallSpinner /> : <FaGoogle className="mr-2 h-5 w-5" />}
                Continue with Google
              </Button>
            )}

            {/* Dev bypass login - form POST so browser stores cookies before redirect (like Google OAuth) */}
            {import.meta.env.DEV && (
              <form
                action="/api/auth/dev-bypass"
                method="POST"
                className="mt-4 space-y-2 rounded-lg border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-800 dark:bg-amber-950/20 z-1000"
              >
                <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
                  Dev bypass (local only)
                </p>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    name="secret"
                    placeholder="Secret (DEV_BYPASS_LOGIN_SECRET)"
                    value={devBypassSecret}
                    onChange={(e) => setDevBypassSecret(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="submit" variant="secondary" size="sm">
                    Bypass
                  </Button>
                </div>
              </form>
            )}

            {/* Additional info */}
            <div className="text-center z-1000 text-xs text-gray-400 dark:text-gray-500">
              <span>By continuing, you agree to our </span>
              <Link
                to="/terms"
                className="underline cursor-pointer underline-offset-2 hover:text-gray-600 dark:hover:text-gray-400"
                target="_blank"
              >
                <span>Terms of Service</span>
              </Link>
              <span> and </span>
              <Link
                to="/privacy"
                className="underline cursor-pointer underline-offset-2 hover:text-gray-600 dark:hover:text-gray-400"
                target="_blank"
              >
                Privacy Policy
              </Link>
              <span>.</span>
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Right side: Marketing message - Dark bg in light mode, light bg in dark mode */}
      <div className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden">
        {/* Enhanced gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-indigo-900/90 to-purple-900/80 dark:from-slate-100 dark:via-indigo-50/90 dark:to-purple-50/80"></div>

        {/* Additional overlay gradient for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-indigo-500/10 dark:from-white/10 dark:via-transparent dark:to-indigo-200/20"></div>

        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div
            className="absolute top-20 right-20 w-96 h-96 rounded-full opacity-30 blur-3xl animate-pulse"
            style={{
              background: `conic-gradient(from 0deg,
        rgba(139, 92, 246, 0.4) 0deg,
        rgba(236, 72, 153, 0.3) 120deg,
        rgba(99, 102, 241, 0.4) 240deg,
        rgba(139, 92, 246, 0.4) 360deg)`,
              animationDuration: '8s',
            }}
          />
          <div
            className="absolute bottom-20 left-20 w-80 h-80 rounded-full opacity-25 blur-3xl animate-pulse"
            style={{
              background: `radial-gradient(ellipse at center,
        rgba(236, 72, 153, 0.4) 0%,
        rgba(139, 92, 246, 0.3) 35%,
        rgba(99, 102, 241, 0.2) 70%,
        transparent 100%)`,
              animationDuration: '12s',
              animationDelay: '2s',
            }}
          />
          <div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-20 blur-3xl animate-pulse"
            style={{
              background: `linear-gradient(45deg,
        rgba(99, 102, 241, 0.3) 0%,
        rgba(139, 92, 246, 0.4) 25%,
        rgba(236, 72, 153, 0.3) 50%,
        rgba(99, 102, 241, 0.2) 75%,
        rgba(139, 92, 246, 0.1) 100%)`,
              animationDuration: '15s',
              animationDelay: '1s',
            }}
          />

          {/* Additional floating gradient orbs */}
          <div
            className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full opacity-15 blur-2xl animate-pulse"
            style={{
              background: `radial-gradient(circle, rgba(236, 72, 153, 0.6) 0%, transparent 70%)`,
              animationDuration: '10s',
              animationDelay: '4s',
            }}
          />
          <div
            className="absolute bottom-1/4 right-1/4 w-40 h-40 rounded-full opacity-10 blur-2xl animate-pulse"
            style={{
              background: `conic-gradient(from 180deg, rgba(99, 102, 241, 0.4), rgba(139, 92, 246, 0.3), rgba(99, 102, 241, 0.4))`,
              animationDuration: '14s',
              animationDelay: '6s',
            }}
          />
        </div>

        {/* Enhanced grid pattern */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.08]">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `
                linear-gradient(rgba(99, 102, 241, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(99, 102, 241, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        <div className="max-w-lg text-center px-8 relative z-10 flex flex-col items-center justify-center h-full">
          <div className="flex justify-center mb-12">
            <CubeLogo size={160} color="white" className="drop-shadow-2xl" />
          </div>
          <h2 className="text-3xl font-bold text-white dark:text-gray-900 bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-600 bg-clip-text text-transparent leading-relaxed">
            Streamline your payroll process with automated solutions. Spend less time on
            calculations and more time growing your business
          </h2>
        </div>
      </div>
    </div>
  )
}

function LoginError({ error }: { error: string | null }) {
  // const searchParams = useSearchParams();
  // const error = searchParams.get("error");

  if (error == null) return null

  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      {/* <AlertDescription>{error}</AlertDescription> */}
    </Alert>
  )
}
